/**
 * PublicLeagueGamePage — /l/:slug/game/:gameId
 * ─────────────────────────────────────────────────────────────────
 * RESTYLED in the league-module cosmetic pass (2026-06-09) to the
 * pubUi design kit — big sporty scoreboard band, period-by-period
 * timeline cards, accent-aware links. Logic unchanged: score, goals
 * and penalties appear only once the scoresheet is locked.
 */

import { useEffect, useState } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import publicApi from '../../services/publicApi.js';
import {
  container, Card, EmptyState,
  INK, MUTED, LINE, SURFACE, NAVY,
  formatTimeNice, accentText,
} from './pubUi.jsx';

export default function PublicLeagueGamePage() {
  const { league, accent } = useOutletContext();
  const { slug, gameId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) return;
    setLoading(true);
    publicApi.get(`/leagues/${encodeURIComponent(slug)}/games/${gameId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug, gameId]);

  useEffect(() => {
    if (data?.game) {
      const g = data.game;
      document.title = `${g.away_team.name} @ ${g.home_team.name} — ${league.name}`;
    }
  }, [data, league.name]);

  if (loading) {
    return <div style={{ ...container, paddingTop: 60, textAlign: 'center', color: MUTED }}>Loading game…</div>;
  }
  if (!data?.game) {
    return (
      <div style={{ ...container, paddingTop: 60, textAlign: 'center', color: MUTED }}>
        <div style={{ fontSize: 34 }}>🥅</div>
        <h1 style={{ color: INK }}>Game not found</h1>
        <Link to={`/l/${slug}/schedule`} style={{ color: accent, fontWeight: 800, textDecoration: 'none' }}>← Schedule</Link>
      </div>
    );
  }

  const { game, goals = [], penalties = [] } = data;
  const locked = game.is_scoresheet_locked;

  const goalsByPeriod = groupByPeriod(goals);
  const penaltiesByPeriod = groupByPeriod(penalties);
  const allPeriods = [...new Set([...goalsByPeriod.keys(), ...penaltiesByPeriod.keys()])].sort((a, b) => a - b);

  return (
    <div>
      {/* ── Scoreboard band ──────────────────────────────────── */}
      <div style={{ background: NAVY, color: '#fff', padding: '36px 0 32px', marginBottom: 34 }}>
        <div style={{ ...container, maxWidth: 860 }}>
          <Link to={`/l/${slug}/schedule`} style={{ color: 'rgba(255,255,255,0.7)', fontWeight: 800, fontSize: 13, textDecoration: 'none' }}>
            ← Schedule
          </Link>
          <div style={{ textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.65)', margin: '10px 0 18px' }}>
            {fmtLongDate(game.game_date)} · {formatTimeNice(game.game_time)}
            {game.venue_name && <> · {game.venue_name}</>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18 }}>
            <TeamBlock team={game.away_team} score={game.away_score} locked={locked} slug={slug} alignRight />
            <div style={{
              flexShrink: 0, textAlign: 'center',
              padding: '7px 16px', borderRadius: 999,
              background: locked ? accent : 'rgba(255,255,255,0.12)',
              color: locked ? accentText(accent) : 'rgba(255,255,255,0.85)',
              fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.1em',
              border: locked ? 'none' : '1px solid rgba(255,255,255,0.25)',
              whiteSpace: 'nowrap',
            }}>
              {locked ? finalLabel(game, goals) : labelFor(game.status)}
            </div>
            <TeamBlock team={game.home_team} score={game.home_score} locked={locked} slug={slug} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
            <span>Away</span><span>Home</span>
          </div>
        </div>
      </div>

      <div style={{ ...container, maxWidth: 860 }}>
        {!locked && (
          <EmptyState icon="⏱">
            Goal-by-goal scoring appears here after the scoresheet is finalized.
          </EmptyState>
        )}

        {locked && allPeriods.map(p => (
          <section key={p} style={{ marginBottom: 26 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
              <span style={{
                padding: '4px 12px', borderRadius: 8, background: NAVY, color: '#fff',
                fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em',
              }}>
                {p === 4 ? 'Overtime' : p === 5 ? 'Shootout' : `Period ${p}`}
              </span>
              <span style={{ flex: 1, height: 1, background: LINE }} />
            </div>

            {(goalsByPeriod.get(p) || []).length > 0 && (
              <Card style={{ marginBottom: 10, overflow: 'hidden' }}>
                <BlockHeading>🚨 Goals</BlockHeading>
                {goalsByPeriod.get(p).map(g => (
                  <GoalRow key={g.id} goal={g} game={game} accent={accent} />
                ))}
              </Card>
            )}

            {(penaltiesByPeriod.get(p) || []).length > 0 && (
              <Card style={{ overflow: 'hidden' }}>
                <BlockHeading>⚖️ Penalties</BlockHeading>
                {penaltiesByPeriod.get(p).map(pen => (
                  <PenaltyRow key={pen.id} penalty={pen} game={game} />
                ))}
              </Card>
            )}
          </section>
        ))}

        {locked && goals.length === 0 && penalties.length === 0 && (
          <EmptyState icon="📋">Final score recorded with no per-event detail.</EmptyState>
        )}
      </div>
    </div>
  );
}

function TeamBlock({ team, score, locked, slug, alignRight }) {
  // Team names deep-link to the team's public page.
  const name = (
    <div style={{ fontSize: 'clamp(16px, 2.6vw, 24px)', fontWeight: 900, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', letterSpacing: '-0.01em' }}>
      {team.name}
    </div>
  );
  return (
    <div style={{ flex: 1, textAlign: alignRight ? 'right' : 'left', minWidth: 0 }}>
      {team.id
        ? <Link to={`/l/${slug}/team/${team.id}`} style={{ textDecoration: 'none' }}>{name}</Link>
        : name}
      {locked && (
        <div style={{ fontSize: 'clamp(44px, 8vw, 72px)', fontWeight: 900, lineHeight: 1, marginTop: 6, color: '#fff' }}>
          {score}
        </div>
      )}
    </div>
  );
}

function BlockHeading({ children }) {
  return (
    <div style={{
      fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.12em',
      fontWeight: 800, padding: '12px 18px 6px',
    }}>
      {children}
    </div>
  );
}

function GoalRow({ goal, game, accent }) {
  const team = goal.team_id === game.home_team.id ? game.home_team.name : game.away_team.name;
  const flags = [];
  if (goal.is_power_play)   flags.push('PP');
  if (goal.is_short_handed) flags.push('SH');
  if (goal.is_empty_net)    flags.push('EN');
  if (goal.is_shootout)     flags.push('SO');
  return (
    <div style={eventRow}>
      <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, color: MUTED, minWidth: 52, fontWeight: 700 }}>
        {goal.time_in_period || '--:--'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, color: INK }}>
          <strong>{goal.scorer?.name || '—'}</strong>
          {goal.assist1 && (
            <span style={{ color: MUTED }}> · A: {goal.assist1.name}{goal.assist2 ? `, ${goal.assist2.name}` : ''}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
          {team}
          {flags.map(f => (
            <span key={f} style={{
              marginLeft: 6, padding: '1px 7px', borderRadius: 5,
              background: `${accent}18`, color: accent, fontSize: 10, fontWeight: 800,
            }}>{f}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function PenaltyRow({ penalty, game }) {
  const team = penalty.team_id === game.home_team.id ? game.home_team.name : game.away_team.name;
  return (
    <div style={eventRow}>
      <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, color: MUTED, minWidth: 52, fontWeight: 700 }}>
        {penalty.time_in_period || '--:--'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14.5, color: INK }}>
          <strong>{penalty.player?.name || '—'}</strong>
          <span style={{ color: MUTED }}> · {penalty.infraction} ({penalty.minutes} min)</span>
          {penalty.is_major && <MiniTag bg="rgba(255,80,80,0.12)" fg="#a8000a">MAJOR</MiniTag>}
          {penalty.is_misconduct && <MiniTag bg="rgba(180,80,255,0.12)" fg="#7c2eb8">MISCONDUCT</MiniTag>}
        </div>
        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{team}</div>
      </div>
    </div>
  );
}

function MiniTag({ bg, fg, children }) {
  return (
    <span style={{
      display: 'inline-block', marginLeft: 6, padding: '1px 7px', borderRadius: 5,
      background: bg, color: fg, fontSize: 10, fontWeight: 800, letterSpacing: '0.05em',
    }}>
      {children}
    </span>
  );
}

function groupByPeriod(events) {
  const m = new Map();
  for (const e of events) {
    if (!m.has(e.period)) m.set(e.period, []);
    m.get(e.period).push(e);
  }
  return m;
}

function finalLabel(game, goals) {
  // Derive OT/SO from goal events (no went_to_overtime column).
  const periodMax = goals.reduce((m, g) => Math.max(m, g.period || 0), 0);
  const hadShootout = goals.some(g => g.is_shootout || g.period === 5);
  if (hadShootout || periodMax === 5) return 'Final · SO';
  if (periodMax === 4)                return 'Final · OT';
  return 'Final';
}
function labelFor(s) {
  if (s === 'upcoming')    return 'Scheduled';
  if (s === 'roster_open') return 'Open';
  if (s === 'locked')      return 'Roster locked';
  if (s === 'completed')   return 'Played';
  return s || '';
}
function fmtLongDate(d) {
  return new Date(d + 'T00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

const eventRow = {
  display: 'flex', gap: 14, padding: '9px 18px',
  borderTop: `1px solid ${SURFACE}`,
};
